import { useCallback } from 'react';
import { useCourse } from '@/contexts/CourseContext';
import { useToast } from '@/hooks/use-toast';
import { courseAPI } from '@/services/api';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType, BorderStyle, ShadingType, PageBreak, WidthType, Table, TableRow, TableCell } from 'docx';
import { capitalizeFirst } from '@/lib/utils';

/**
 * useCourseExports Hook
 * 
 * Supports both Start-From-Scratch and Import flows:
 * - If courseData and courseId are provided, uses them (Import flow)
 * - Otherwise, uses CourseContext (Start-From-Scratch flow)
 * 
 * @param {string} courseId - Optional course ID for Import flow (for Moodle export)
 * @param {Object} courseData - Optional course data for Import flow
 */
export function useCourseExports(courseId, courseData) {
  // Get context (for Start-From-Scratch flow)
  const contextData = useCourse();
  const contextCourse = contextData?.course;

  // Use prop data if provided (Import flow), otherwise use context (Start-From-Scratch)
  const course = courseData || contextCourse;
  const effectiveCourseId = courseId || course?.id;

  const { toast } = useToast();

  const download = useCallback((filename, mimeType, data) => {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const buildCoursePlainText = useCallback(() => {
    if (!course) return '';
    let text = '';
    if (course.title) text += `${course.title}\n`;
    if (course.subjectArea) text += `Subject: ${course.subjectArea}\n`;
    if (course.educationalLevel) text += `Level: ${course.educationalLevel}\n`;
    if (course.grade) text += `Grade: ${course.grade}\n`;
    if (course.totalHours > 0) text += `Duration: ${course.totalHours} hours\n`;
    if (course.description) text += `\n${course.description}\n\n`;
    const goals = course.learningObjectives || course.goals || [];
    if (goals.length > 0) {
      text += `LEARNING GOALS\n`;
      goals.forEach((goal, index) => {
        text += `${index + 1}. ${goal.text}\n`;
      });
    }
    if (course.topics && course.topics.length > 0) {
      text += `\nTOPICS & ACTIVITIES\n`;
      course.topics.forEach(topic => {
        text += `\n${topic.title}\n`;
        topic.activities.forEach(activity => {
          // Support both new (objectiveId) and legacy (goalId) field names
          const activityObjectiveId = activity.objectiveId || activity.goalId;
          const allGoals = course.learningObjectives || course.goals || [];
          const goalIndex = allGoals.findIndex(g => g.id === activityObjectiveId);
          const goalText = allGoals.find(g => g.id === activityObjectiveId)?.text || 'No goal assigned';
          const goalLabel = typeof goalIndex === 'number' && goalIndex > -1 ? `Goal ${goalIndex + 1}: ${goalText}` : `Goal: ${goalText}`;
          text += `  • ${activity.title}\n`;
          text += `    ${goalLabel}\n`;
          if (activity.description) text += `    ${activity.description}\n`;
          text += `    Actor: ${capitalizeFirst(activity.actor || '')} | Resource: ${activity.resourceType || ''}\n`;
          if (activity.resourceDescription) text += `    ${activity.resourceDescription}\n`;
          text += `\n`;
        });
      });
    }
    return text;
  }, [course]);

  const buildCourseHtml = useCallback(() => {
    const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const allGoals = course?.learningObjectives || course?.goals || [];
    const goalsRows = allGoals.map((g, i) => `<tr><td class="idx">${i + 1}</td><td>${esc(g.text)}</td></tr>`).join('');
    const topicsHtml = (course?.topics || []).map((t, ti) => {
      const acts = (t.activities || []).map((a) => {
        // Support both new (objectiveId) and legacy (goalId) field names
        const activityObjectiveId = a.objectiveId || a.goalId;
        const allGoals = course?.learningObjectives || course?.goals || [];
        const goalIndex = allGoals.findIndex(g => g.id === activityObjectiveId);
        const goalText = allGoals.find(g => g.id === activityObjectiveId)?.text;
        return `
          <div class="activity">
            <div class="activity-header">
              <span class="activity-title">${esc(a.title)}</span>
              ${typeof goalIndex === 'number' && goalIndex > -1 ? `<span class="badge badge-goal">Goal ${goalIndex + 1}</span>` : ''}
              ${a.actor ? `<span class="badge badge-actor">${esc(capitalizeFirst(a.actor))}</span>` : ''}
              ${a.resourceType ? `<span class="badge badge-resource">${esc(a.resourceType)}</span>` : ''}
            </div>
            ${goalText ? `<div class="goal-ref">🎯 ${esc(goalText)}</div>` : ''}
            ${a.description ? `<p class="activity-desc">${esc(a.description)}</p>` : ''}
            ${a.facilitatorRole ? `<div class="facilitator"><span>Facilitator:</span> ${esc(a.facilitatorRole)}</div>` : ''}
            ${a.resourceTitle ? `<div class="resource"><span>Resource:</span> ${esc(a.resourceTitle)}${a.resourceDescription ? ` – <em>${esc(a.resourceDescription)}</em>` : ''}</div>` : ''}
          </div>`;
      }).join('');
      return `<section class="topic"><h3><span class="topic-idx">${ti + 1}</span> ${esc(t.title)}</h3>${acts || '<p class="muted">No activities added yet.</p>'}</section>`;
    }).join('');
    const title = esc(course?.title || 'Course');
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body{font-family:Segoe UI,Arial,Helvetica,sans-serif;color:#111;line-height:1.5;margin:40px}
    h1{font-size:28px;margin:0 0 6px}
    .meta{color:#555;margin-bottom:16px}
    h2{margin-top:28px;border-bottom:2px solid #eee;padding-bottom:6px}
    h3{margin:18px 0 8px}
    .topic{margin:12px 0 18px;padding:12px 14px;border:1px solid #eee;border-radius:10px;background:#fafbfd}
    .topic-idx{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:#f59e0b;color:#fff;font-weight:700;margin-right:8px}
    .goals{width:100%;border-collapse:collapse;margin:8px 0}
    .goals th,.goals td{border:1px solid #e5e7eb;padding:8px 10px;text-align:left}
    .goals th{background:#f9fafb}
    .goals .idx{width:48px;text-align:center;color:#555}
    .activity{padding:10px 12px;border:1px solid #eef2f7;border-radius:8px;background:#fff;margin:10px 0}
    .activity-header{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
    .activity-title{font-weight:700}
    .badge{display:inline-block;padding:3px 8px;border-radius:999px;font-size:12px}
    .badge-goal{background:#ede9fe;color:#6d28d9}
    .badge-actor{background:#ffe4e6;color:#be123c}
    .badge-resource{background:#dcfce7;color:#166534}
    .goal-ref{color:#6d28d9;margin-top:4px}
    .activity-desc{margin:6px 0}
    .facilitator span,.resource span{font-weight:600;color:#374151}
    .muted{color:#6b7280}
  </style>
  </head>
  <body>
    <h1>${title}</h1>
    <div class="meta"><strong>Subject:</strong> ${esc(course?.subjectArea)} &nbsp; | &nbsp; <strong>Level:</strong> ${esc(course?.educationalLevel)} &nbsp; | &nbsp; <strong>Grade:</strong> ${esc(course?.grade)} &nbsp; | &nbsp; <strong>Duration:</strong> ${esc(course?.totalHours)} hours</div>
    ${course?.description ? `<p>${esc(course.description)}</p>` : ''}
    <h2>Learning Goals</h2>
    ${allGoals.length > 0 ? `<table class="goals"><thead><tr><th>#</th><th>Goal</th></tr></thead><tbody>${goalsRows}</tbody></table>` : '<p class="muted">No goals defined.</p>'}
    <h2>Topics & Activities</h2>
    ${topicsHtml || '<p class="muted">No topics yet.</p>'}
  </body>
</html>`;
  }, [course]);


  const transformCourseData = useCallback(() => {
    if (!course) return null;

    // Transform course data to the requested format
    return {
      CourseInfo: {
        Title: course.title || '',
        Description: course.description || '',
        Subjectarea: course.subjectArea || '',
        Level: course.educationalLevel || '',
        Grade: course.grade || '',
        Duration: course.totalHours || 0
      },
      Topics: (course.topics || []).map(topic => ({
        TopicName: topic.title || '',
        Activities: (topic.activities || []).map(activity => {
          // Find the goal text(s) for this activity
          const goalTexts = [];
          // Support both new (objectiveId) and legacy (goalId) field names
          const activityObjectiveId = activity.objectiveId || activity.goalId;
          const allGoals = course.learningObjectives || course.goals;
          if (activityObjectiveId && allGoals) {
            const goal = allGoals.find(g => g.id === activityObjectiveId);
            if (goal && goal.text) {
              goalTexts.push(goal.text);
            }
          }

          return {
            id: activity.id || '',
            Title: activity.title || '',
            Description: activity.description || '',
            BloomType: activity.bloomLevel || '',
            Goals: goalTexts,
            ResourceType: activity.resourceType || '',
            Actor: capitalizeFirst(activity.actor || ''),
            FacilitatorRole: activity.facilitatorRole || '',
            ResourceTitle: activity.resourceTitle || '',
            ResourceDescription: activity.resourceDescription || '',
            Duration: activity.duration || null
          };
        })
      }))
    };
  }, [course]);

  const transformCourseDataToCanonicalCdm = useCallback(() => {
    if (!course) return null;

    const allGoals = course.learningObjectives || course.goals || [];
    const topics = course.topics || [];

    // Diagnostic: Log bloom level distribution for debugging
    const bloomDistribution = {
      Remembering: allGoals.filter(g => g.bloomLevel === 'Remembering').length,
      Understanding: allGoals.filter(g => g.bloomLevel === 'Understanding').length,
      Applying: allGoals.filter(g => g.bloomLevel === 'Applying').length,
      Analyzing: allGoals.filter(g => g.bloomLevel === 'Analyzing').length,
      Evaluating: allGoals.filter(g => g.bloomLevel === 'Evaluating').length,
      Creating: allGoals.filter(g => g.bloomLevel === 'Creating').length,
      nullOrMissing: allGoals.filter(g => !g.bloomLevel).length
    };
    console.log('🔍 CDM Export - Bloom level distribution:', bloomDistribution);
    console.log('🔍 CDM Export - All goals with bloom levels:', allGoals.map(g => ({ text: g.text?.substring(0, 50), bloomLevel: g.bloomLevel })));

    // Calculate total duration in minutes
    // DurationNumber in CDM format is in minutes
    let totalDurationMinutes = 0;
    if (course.totalHours && course.totalHours > 0) {
      // Convert hours to minutes
      totalDurationMinutes = Math.round(course.totalHours * 60);
    } else {
      // Calculate from sum of all activity durations
      topics.forEach(topic => {
        (topic.activities || []).forEach(activity => {
          if (activity.duration && activity.duration > 0) {
            totalDurationMinutes += activity.duration;
          }
        });
      });
    }

    // Build LessonInfo
    const lessonInfo = {
      StrategyName: course.title || '',
      Description: course.description || null,
      DurationNumber: totalDurationMinutes,
      EducationLevel: course.educationalLevel || '',
      SubjectArea: course.subjectArea || '',
      Grade: course.grade || null,
      Goals: allGoals.map(g => g.text || '').filter(Boolean),
      GoalsRemembering: allGoals.filter(g => g.bloomLevel === 'Remembering').map(g => g.text || '').filter(Boolean),
      GoalsUnderstanding: allGoals.filter(g => g.bloomLevel === 'Understanding').map(g => g.text || '').filter(Boolean),
      GoalsApplying: allGoals.filter(g => g.bloomLevel === 'Applying').map(g => g.text || '').filter(Boolean),
      GoalsAnalyzing: allGoals.filter(g => g.bloomLevel === 'Analyzing').map(g => g.text || '').filter(Boolean),
      GoalsEvaluating: allGoals.filter(g => g.bloomLevel === 'Evaluating').map(g => g.text || '').filter(Boolean),
      GoalsCreating: allGoals.filter(g => g.bloomLevel === 'Creating').map(g => g.text || '').filter(Boolean),
      Actors: [],
      Learners: [],
      StaffRoles: [],
      Prerequisites: []
    };

    // Collect unique actors from activities
    const actorsSet = new Set();
    const learnersSet = new Set();
    const staffRolesSet = new Set();

    // Build FlowSub (topic phases) first to calculate phase positions
    const flowSub = topics.map((topic, index) => {
      // Calculate phase time (sum of activity durations in this topic)
      const phaseTime = (topic.activities || []).reduce((sum, act) => {
        return sum + (act.duration || 0);
      }, 0);

      return {
        top: 200.673543534 + index * 700, // Staggered vertical positions
        left: 0,
        type: 'flowPhase',
        text: topic.title || `Topic ${index + 1}`,
        id: `flowPhase${index + 1}`,
        phaseTime: phaseTime
      };
    });

    // Build ConceptualBase (activities) with positions aligned to FlowSub phases
    const conceptualBase = [];
    let itemCounter = 0; // Counter for all items (activities and resources)
    const activityIdMap = new Map(); // Map activity.id to widgetItem ID

    topics.forEach((topic, topicIndex) => {
      const phaseTop = flowSub[topicIndex]?.top || (200.673543534 + topicIndex * 700);
      const nextPhaseTop = topicIndex < flowSub.length - 1 
        ? flowSub[topicIndex + 1].top 
        : phaseTop + 700; // Default spacing if last topic
      
      // Calculate spacing for activities within this topic's phase range
      const activitiesInTopic = (topic.activities || []).length;
      // Ensure activities stay well within phase bounds (leave 150px margin at top and bottom)
      const availableRange = nextPhaseTop - phaseTop - 300; // 150px margin on each side
      const activitySpacing = activitiesInTopic > 1 
        ? Math.max(50, availableRange / activitiesInTopic) // Minimum 50px spacing
        : 200; // Default spacing if only one activity
      
      (topic.activities || []).forEach((activity, activityIndex) => {
        // Activities use odd-numbered IDs (1, 3, 5, 7...)
        itemCounter++;
        const widgetId = `widgetItem${itemCounter}`;
        activityIdMap.set(activity.id, widgetId);

        // Position activity within its topic's phase range
        // Start from phaseTop + 150px margin, ensuring it's > phaseTop and < nextPhaseTop
        const activityTop = Math.min(
          phaseTop + 150 + (activityIndex * activitySpacing),
          nextPhaseTop - 150 // Ensure we don't exceed next phase boundary
        );

        // Get goal text(s) for this activity
        const activityObjectiveId = activity.objectiveId || activity.goalId;
        const goalTexts = [];
        if (activityObjectiveId && allGoals) {
          const goal = allGoals.find(g => g.id === activityObjectiveId);
          if (goal && goal.text) {
            goalTexts.push(goal.text);
          }
        }

        // Track actors - normalize group variations to "Group"
        let actor = capitalizeFirst(activity.actor || '');
        if (actor) {
          // Normalize group variations to "Group"
          const actorLower = actor.toLowerCase().trim();
          if (actorLower.includes('group') || 
              actorLower === 'expert group' || 
              actorLower === 'pair group' ||
              actorLower === 'expertgroup' ||
              actorLower === 'pairgroup') {
            actor = 'Group';
          }
          
          actorsSet.add(actor);
          if (actor.toLowerCase() === 'student' || actor.toLowerCase() === 'group') {
            learnersSet.add(actor);
          } else {
            staffRolesSet.add(actor);
          }
        }

        // Track facilitators - add "Facilitator" to actors if facilitatorRole exists
        if (activity.facilitatorRole) {
          actorsSet.add('Facilitator');
          staffRolesSet.add('Facilitator');
          staffRolesSet.add('Teacher');
          
          // Also check if facilitatorRole contains an actor name (e.g., "Teacher")
          const facilitatorRole = activity.facilitatorRole.trim();
          const facilitatorActor = capitalizeFirst(facilitatorRole);
          // If the facilitator role is a common actor name, add it
          const commonActors = ['Teacher', 'Instructor', 'Tutor', 'Mentor'];
          if (commonActors.some(common => facilitatorActor.toLowerCase().includes(common.toLowerCase()))) {
            actorsSet.add(facilitatorActor);
            staffRolesSet.add(facilitatorActor);
          }
        }

        // Build resource child if resource exists
        const children = [];
        if (activity.resourceTitle || activity.resourceType) {
          // Resources use even-numbered IDs (2, 4, 6, 8...)
          itemCounter++;
          const resourceId = `widgetItem${itemCounter}`;
          children.push({
            id: resourceId,
            type: 'activity-resource',
            top: activityTop + 300, // Position resource below activity
            left: 100 + (activityIndex % 5) * 100, // Distribute horizontally
            linkToBase: false,
            children: [],
            ModalData: {
              Title: activity.resourceTitle || '',
              Author: null,
              Description: activity.resourceDescription || '',
              Type: activity.resourceType || '',
              ResourceLocation: null,
              IsStoredFile: false
            }
          });
        }

        // Build activity in ConceptualBase format
        const conceptualActivity = {
          id: widgetId,
          type: 'activity-simple',
          top: activityTop, // Positioned within topic's phase range
          left: 100 + (activityIndex % 5) * 100, // Horizontal distribution
          linkToBase: true,
          children: children,
          ModalData: {
            Title: activity.title || 'Untitled Activity',
            Description: activity.description || '',
            LearningGoal: goalTexts,
            Type: activity.bloomLevel || '',
            Actor: actor || undefined,
            Facilitator: activity.facilitatorRole ? "Teacher" : undefined,
            FacilitatorRole: activity.facilitatorRole || undefined,
            TimeLimit: activity.duration || null
          }
        };

        conceptualBase.push(conceptualActivity);
      });
    });

    // Update LessonInfo with collected actors
    lessonInfo.Actors = Array.from(actorsSet);
    lessonInfo.Learners = Array.from(learnersSet);
    lessonInfo.StaffRoles = Array.from(staffRolesSet);

    // Build FlowBase (actor lanes) with positions aligned to FlowSub phases
    const flowBase = [];
    const actorLanes = new Map();

    // Group activities by actor
    topics.forEach((topic, topicIndex) => {
      const phaseTop = flowSub[topicIndex]?.top || (200.673543534 + topicIndex * 700);
      const nextPhaseTop = topicIndex < flowSub.length - 1 
        ? flowSub[topicIndex + 1].top 
        : phaseTop + 700;
      
      const activitiesInTopic = (topic.activities || []).length;
      const availableRange = nextPhaseTop - phaseTop - 300; // 150px margin on each side
      const activitySpacing = activitiesInTopic > 1 
        ? Math.max(50, availableRange / activitiesInTopic) // Minimum 50px spacing
        : 200;

      (topic.activities || []).forEach((activity, activityIndex) => {
        const actor = capitalizeFirst(activity.actor || '');
        if (!actor) return;

        if (!actorLanes.has(actor)) {
          actorLanes.set(actor, {
            ActorName: actor,
            ActorX: actorLanes.size + 1, // 1, 3, 5, etc. (odd numbers for spacing)
            Activities: []
          });
        }

        const lane = actorLanes.get(actor);
        const widgetId = activityIdMap.get(activity.id);
        if (widgetId) {
          // Position activity within topic's phase range, matching ConceptualBase
          const activityTop = Math.min(
            phaseTop + 150 + (activityIndex * activitySpacing),
            nextPhaseTop - 150 // Ensure we don't exceed next phase boundary
          );
          lane.Activities.push({
            id: widgetId,
            title: activity.title || '',
            top: activityTop, // Aligned with ConceptualBase position
            TimeLimit: activity.duration || null
          });
        }
      });
    });

    // Add facilitator lanes if there are facilitator roles
    topics.forEach((topic, topicIndex) => {
      const phaseTop = flowSub[topicIndex]?.top || (200.673543534 + topicIndex * 700);
      const nextPhaseTop = topicIndex < flowSub.length - 1 
        ? flowSub[topicIndex + 1].top 
        : phaseTop + 700;
      const activitiesInTopic = (topic.activities || []).length;
      const availableRange = nextPhaseTop - phaseTop - 300;
      const activitySpacing = activitiesInTopic > 1 
        ? Math.max(50, availableRange / activitiesInTopic)
        : 200;

      (topic.activities || []).forEach((activity, activityIndex) => {
        if (activity.facilitatorRole) {
          const facilitatorName = 'Facilitator';
          if (!actorLanes.has(facilitatorName)) {
            actorLanes.set(facilitatorName, {
              ActorName: facilitatorName,
              ActorX: actorLanes.size + 1,
              Activities: []
            });
          }

          const lane = actorLanes.get(facilitatorName);
          const facilitatorId = `FacilitatorId${lane.Activities.length + 1}`;
          // Position facilitator activity within topic's phase range
          const facilitatorTop = Math.min(
            phaseTop + 150 + (activityIndex * activitySpacing),
            nextPhaseTop - 150
          );
          lane.Activities.push({
            id: facilitatorId,
            title: activity.facilitatorRole,
            top: facilitatorTop
          });
        }
      });
    });

    flowBase.push(...Array.from(actorLanes.values()));

    // Build LessonInfoExtras
    const lessonInfoExtras = {
      Simple_activity_types: [
        'Creating',
        'Evaluating',
        'Analyzing',
        'Applying',
        'Understanding',
        'Remembering'
      ],
      Resource_types: [
        'Hypertext',
        'Audio',
        'Video',
        'Image',
        'Document',
        'Assessment',
        'Forum',
        'Quiz',
        'Wiki',
        'Poll',
        'Chat',
        'Page (Moodle)',
        'Book (Moodle)',
        'Glossary (Moodle)',
        'Database (Moodle)',
        'Lesson (Moodle)',
        'Survey (Moodle)',
        'Feedback (Moodle)',
        'H5P (Moodle)',
        'Other'
      ],
      Resource_copyright: ['free', 'proprietary']
    };

    // Return canonical CDM structure
    return {
      data: {
        LessonInfo: lessonInfo,
        Conceptual: {
          ConceptualBase: conceptualBase
        },
        Flow: {
          FlowSub: flowSub,
          FlowBase: flowBase
        },
        LessonInfoExtras: lessonInfoExtras,
        Analyzer: []
      }
    };
  }, [course]);

  const handleExportJson = useCallback(() => {
    if (!course) return;

    const payload = transformCourseData();
    if (!payload) return;

    download(`${course.title || 'course'}.json`, 'application/json', JSON.stringify(payload, null, 2));
    toast({ title: 'Exported', description: 'JSON file downloaded' });
  }, [course, download, toast, transformCourseData]);

  const handleExportCdm = useCallback(async () => {
    if (!course) return;

    const payload = transformCourseDataToCanonicalCdm();
    if (!payload) return;

    try {
      // Import JSZip dynamically
      const JSZip = (await import('jszip')).default;

      // Create a new ZIP file
      const zip = new JSZip();

      // Add the JSON data to the ZIP file with the name 'source.json'
      // This matches the expected CDM format where the ZIP contains a JSON file
      // (preferably named 'source.json' as per EvaluationWebApp standards)
      zip.file('source.json', JSON.stringify(payload, null, 2));

      // Generate the ZIP file as a blob
      const blob = await zip.generateAsync({ type: 'blob' });

      // Download the ZIP file with .cdm extension
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${course.title || 'course'}.cdm`;
      a.click();
      URL.revokeObjectURL(url);

      toast({ title: 'Exported', description: 'CDM file downloaded' });
    } catch (error) {
      console.error('Error exporting CDM:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export CDM file',
        variant: 'destructive'
      });
    }
  }, [course, toast, transformCourseDataToCanonicalCdm]);

  const handleExportDoc = useCallback(async () => {
    if (!course) return;

    try {
      const children = [];

      // Cover page - simplified approach
      const metaParts = [];
      if (course.subjectArea) metaParts.push(`Subject: ${course.subjectArea}`);
      if (course.educationalLevel) metaParts.push(`Level: ${course.educationalLevel}`);
      if (course.grade) metaParts.push(`Grade: ${course.grade}`);
      if (course.totalHours) metaParts.push(`Duration: ${course.totalHours} hours`);

      // Cover page with table for background color - let it size naturally to avoid black pages
      const coverTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE, size: 0 },
          bottom: { style: BorderStyle.NONE, size: 0 },
          left: { style: BorderStyle.NONE, size: 0 },
          right: { style: BorderStyle.NONE, size: 0 },
          insideHorizontal: { style: BorderStyle.NONE, size: 0 },
          insideVertical: { style: BorderStyle.NONE, size: 0 }
        },
        rows: [
          new TableRow({
            height: {
              value: 16838, // A4 height in twips (297mm) - exact to fill page
              rule: 'exact'
            },
            children: [
              new TableCell({
                children: [
                  // Add spacing at top to center content vertically on the page
                  new Paragraph({ spacing: { before: 4000 } }),
                  // Title
                  ...(course.title ? [new Paragraph({
                    children: [
                      new TextRun({
                        text: course.title,
                        bold: true,
                        size: 56,
                        color: 'FFFFFF'
                      })
                    ],
                    spacing: { before: 0, after: 400 },
                    alignment: AlignmentType.CENTER
                  })] : []),
                  // Metadata
                  ...(metaParts.length > 0 ? [new Paragraph({
                    children: [
                      new TextRun({
                        text: metaParts.join('  •  '),
                        size: 20,
                        color: 'FFFFFF'
                      })
                    ],
                    spacing: { after: 600 },
                    alignment: AlignmentType.CENTER
                  })] : []),
                  // Description
                  ...(course.description ? [new Paragraph({
                    children: [
                      new TextRun({
                        text: course.description,
                        size: 26,
                        color: 'FFFFFF'
                      })
                    ],
                    spacing: { before: 600, after: 0 },
                    alignment: AlignmentType.LEFT,
                    border: {
                      left: {
                        color: 'FFFFFF',
                        size: 40,
                        style: BorderStyle.SINGLE
                      }
                    },
                    indent: { left: 1200, right: 1200 }
                  })] : []),
                  // Add large spacing at bottom to fill the page and push content up
                  new Paragraph({ spacing: { after: 10000 } })
                ],
                shading: {
                  type: ShadingType.SOLID,
                  color: 'F59E0B',
                  fill: 'F59E0B'
                },
                margins: {
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0
                }
              })
            ]
          })
        ]
      });

      // Cover page section
      const coverChildren = [coverTable];

      // Content section - Section header
      const contentChildren = [
        new Paragraph({
          children: [
            new TextRun({
              text: 'Topics & Activities',
              bold: true,
              size: 44,
              color: '1a202c'
            })
          ],
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 600, after: 800 },
          border: {
            bottom: {
              color: 'f59e0b',
              size: 240,
              style: BorderStyle.SINGLE
            }
          }
        })
      ];

      // Topics and activities
      if (course.topics && course.topics.length > 0) {
        course.topics.forEach((topic, topicIndex) => {
          // Add page break before topic if it's not the first topic
          // This ensures topics start in the top 40% of the page (at the top of a new page)
          if (topicIndex > 0) {
            contentChildren.push(new Paragraph({
              pageBreakBefore: true,
              children: []
            }));
          }

          // Topic header - clean style matching PDF
          const topicHeaderTable = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE, size: 0 },
              bottom: { style: BorderStyle.NONE, size: 0 },
              left: { style: BorderStyle.NONE, size: 0 },
              right: { style: BorderStyle.NONE, size: 0 },
              insideHorizontal: { style: BorderStyle.NONE, size: 0 },
              insideVertical: { style: BorderStyle.NONE, size: 0 }
            },
            rows: [
              new TableRow({
                cantSplit: true, // Keep topic header together
                children: [
                  new TableCell({
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${topicIndex + 1}`,
                            bold: true,
                            size: 32,
                            color: 'FFFFFF'
                          })
                        ],
                        spacing: { before: 200, after: 200 },
                        alignment: AlignmentType.CENTER
                      })
                    ],
                    width: { size: 15, type: WidthType.PERCENTAGE },
                    shading: {
                      type: ShadingType.SOLID,
                      color: 'F59E0B',
                      fill: 'F59E0B'
                    },
                    margins: {
                      top: 400,
                      bottom: 400,
                      left: 400,
                      right: 200
                    }
                  }),
                  new TableCell({
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: topic.title || `Topic ${topicIndex + 1}`,
                            bold: true,
                            size: 36,
                            color: 'FFFFFF'
                          })
                        ],
                        spacing: { before: 200, after: 200 }
                      })
                    ],
                    width: { size: 85, type: WidthType.PERCENTAGE },
                    shading: {
                      type: ShadingType.SOLID,
                      color: 'F59E0B',
                      fill: 'F59E0B'
                    },
                    margins: {
                      top: 400,
                      bottom: 400,
                      left: 200,
                      right: 400
                    }
                  })
                ]
              })
            ]
          });

          contentChildren.push(topicHeaderTable);
          contentChildren.push(new Paragraph({ spacing: { after: 400 } }));

          // Activities
          if (topic.activities && topic.activities.length > 0) {
            topic.activities.forEach((activity, activityIndex) => {
              // Activity card container
              // Use cantSplit on table row to ensure activity doesn't split across pages
              // Word will automatically move the activity to next page if it doesn't fit
              const activityTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
                  left: { style: BorderStyle.SINGLE, size: 4, color: '4299E1' },
                  right: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
                  insideHorizontal: { style: BorderStyle.NONE, size: 0 },
                  insideVertical: { style: BorderStyle.NONE, size: 0 }
                },
                rows: [
                  new TableRow({
                    cantSplit: true, // Prevent table row from splitting across pages
                    children: [
                      new TableCell({
                        children: [
                          // Activity title with badges
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: activity.title || '',
                                bold: true,
                                size: 32,
                                color: '1a202c'
                              }),
                              // Support both new (objectiveId) and legacy (goalId) field names
                              ...((activity.objectiveId || activity.goalId) ? (() => {
                                const activityObjectiveId = activity.objectiveId || activity.goalId;
                                const allGoals = course.learningObjectives || course.goals || [];
                                const goalIndex = allGoals.findIndex(g => g.id === activityObjectiveId);
                                if (goalIndex !== undefined && goalIndex > -1) {
                                  return [new TextRun({ text: ' ', size: 24 }), new TextRun({
                                    text: `Goal ${goalIndex + 1}`,
                                    bold: true,
                                    size: 20,
                                    color: '6d28d9'
                                  })];
                                }
                                return [];
                              })() : [])
                            ],
                            spacing: { before: 400, after: 200 },
                            keepTogether: true // Keep activity title with rest of content
                          }),
                          // Badges row
                          ...((activity.actor || activity.resourceType) ? [new Paragraph({
                            children: [
                              ...(activity.actor ? [
                                new TextRun({
                                  text: capitalizeFirst(activity.actor),
                                  size: 20,
                                  color: 'be123c',
                                  bold: true
                                }),
                                ...(activity.resourceType ? [new TextRun({ text: '  ' })] : [])
                              ] : []),
                              ...(activity.resourceType ? [
                                new TextRun({
                                  text: activity.resourceType,
                                  size: 20,
                                  color: '166534',
                                  bold: true
                                })
                              ] : [])
                            ],
                            spacing: { after: 300 }
                          })] : []),
                          // Goal box - support both new (objectiveId) and legacy (goalId) field names
                          ...((activity.objectiveId || activity.goalId) ? (() => {
                            const activityObjectiveId = activity.objectiveId || activity.goalId;
                            const allGoals = course.learningObjectives || course.goals || [];
                            const goal = allGoals.find(g => g.id === activityObjectiveId);
                            if (goal?.text) {
                              return [new Paragraph({
                                children: [
                                  new TextRun({
                                    text: 'Learning Goal: ',
                                    bold: true,
                                    size: 24,
                                    color: '6d28d9'
                                  }),
                                  new TextRun({
                                    text: goal.text,
                                    size: 24,
                                    color: '4a5568'
                                  })
                                ],
                                spacing: { before: 200, after: 300 },
                                shading: {
                                  type: ShadingType.SOLID,
                                  color: 'f3f4f6',
                                  fill: 'f3f4f6'
                                },
                                border: {
                                  left: {
                                    color: '6d28d9',
                                    size: 40,
                                    style: BorderStyle.SINGLE
                                  }
                                },
                                indent: { left: 200 }
                              })];
                            }
                            return [];
                          })() : []),
                          // Description
                          ...(activity.description ? [new Paragraph({
                            children: [
                              new TextRun({
                                text: activity.description,
                                size: 22,
                                color: '2d3748'
                              })
                            ],
                            spacing: { after: 300 }
                          })] : []),
                          // Facilitator
                          ...(activity.facilitatorRole ? [new Paragraph({
                            children: [
                              new TextRun({
                                text: 'Facilitator: ',
                                bold: true,
                                size: 20,
                                color: '2d3748'
                              }),
                              new TextRun({
                                text: activity.facilitatorRole,
                                size: 20,
                                color: '718096',
                                italics: true
                              })
                            ],
                            spacing: { before: 200, after: 200 },
                            border: {
                              top: {
                                color: 'e2e8f0',
                                size: 20,
                                style: BorderStyle.SINGLE
                              }
                            }
                          })] : []),
                          // Resource
                          ...(activity.resourceTitle ? [new Paragraph({
                            children: [
                              new TextRun({
                                text: 'Resource: ',
                                bold: true,
                                size: 20,
                                color: '2d3748'
                              }),
                              new TextRun({
                                text: activity.resourceTitle,
                                size: 20,
                                color: '2d3748'
                              }),
                              ...(activity.resourceDescription ? [
                                new TextRun({
                                  text: ` – ${activity.resourceDescription}`,
                                  size: 20,
                                  color: '718096',
                                  italics: true
                                })
                              ] : [])
                            ],
                            spacing: {
                              after: 400,
                              before: activity.facilitatorRole ? 0 : 200
                            },
                            border: activity.facilitatorRole ? {} : {
                              top: {
                                color: 'e2e8f0',
                                size: 20,
                                style: BorderStyle.SINGLE
                              }
                            }
                          })] : [])
                        ],
                        margins: {
                          top: 400,
                          bottom: 400,
                          left: 400,
                          right: 400
                        }
                      })
                    ]
                  })
                ]
              });

              contentChildren.push(activityTable);
              // Only add spacing if this is not the last activity in the topic
              // This prevents blank pages when the next topic starts with a page break
              if (activityIndex < topic.activities.length - 1) {
                contentChildren.push(new Paragraph({ spacing: { after: 600 } }));
              }
            });
          } else {
            contentChildren.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'No activities added yet.',
                    italics: true,
                    size: 22,
                    color: 'a0aec0'
                  })
                ],
                spacing: { after: 600 }
              })
            );
          }
        });
      }

      // Create document with two sections: cover page and content
      const doc = new Document({
        sections: [
          // Cover page section - minimal margins to use full page
          {
            children: coverChildren,
            properties: {
              page: {
                margin: {
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0
                }
              }
            }
          },
          // Content section - normal margins
          {
            children: contentChildren,
            properties: {
              page: {
                margin: {
                  top: 1440,
                  right: 1440,
                  bottom: 1440,
                  left: 1440
                }
              }
            }
          }
        ]
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${course.title || 'course'}.docx`;
      a.click();
      URL.revokeObjectURL(url);

      toast({ title: 'Exported', description: 'Word (.docx) file downloaded' });
    } catch (error) {
      console.error('Error exporting to Word:', error);
      toast({
        title: 'Export Error',
        description: 'Failed to export Word document',
        variant: 'destructive'
      });
    }
  }, [course, toast]);

  const buildCoursePdfHtml = useCallback(() => {
    const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const topicsHtml = (course?.topics || []).map((t, ti) => {
      const acts = (t.activities || []).map((a) => {
        // Support both new (objectiveId) and legacy (goalId) field names
        const activityObjectiveId = a.objectiveId || a.goalId;
        const allGoals = course?.learningObjectives || course?.goals || [];
        const goalIndex = allGoals.findIndex(g => g.id === activityObjectiveId);
        const goalText = allGoals.find(g => g.id === activityObjectiveId)?.text;
        const goalBadge = typeof goalIndex === 'number' && goalIndex > -1 ? `<span class="goal-badge">Goal ${goalIndex + 1}</span>` : '';
        const actorBadge = a.actor ? `<span class="actor-badge">${esc(a.actor)}</span>` : '';
        const resourceBadge = a.resourceType ? `<span class="resource-badge">${esc(a.resourceType)}</span>` : '';

        return `
          <div class="activity-card" data-activity-id="${a.id}">
            <div class="activity-header">
              <h4 class="activity-title">${esc(a.title)}</h4>
              <div class="badges">
                ${goalBadge}
                ${actorBadge}
                ${resourceBadge}
              </div>
            </div>
            ${goalText ? `
              <div class="goal-box">
                <div class="goal-content">
                  <strong>Learning Goal:</strong> ${esc(goalText)}
                </div>
              </div>
            ` : ''}
            ${a.description ? `<p class="activity-description">${esc(a.description)}</p>` : ''}
            ${a.facilitatorRole ? `<div class="meta-info"><strong>Facilitator:</strong> <em>${esc(a.facilitatorRole)}</em></div>` : ''}
            ${a.resourceTitle ? `
              <div class="meta-info">
                <strong>Resource:</strong> ${esc(a.resourceTitle)}${a.resourceDescription ? ` <em>– ${esc(a.resourceDescription)}</em>` : ''}
              </div>
            ` : ''}
          </div>`;
      }).join('');

      return `
        <div class="topic-section" data-topic-id="${t.id}">
          <div class="topic-header">
            <span class="topic-number">${ti + 1}</span>
            <h3 class="topic-title">${esc(t.title)}</h3>
          </div>
          <div class="activities-container">
            ${acts || '<p class="empty-state">No activities added yet.</p>'}
          </div>
        </div>`;
    }).join('');

    const title = esc(course?.title || 'Course');
    const metaParts = [];
    if (course?.subjectArea) metaParts.push(`<span><strong>Subject:</strong> ${esc(course.subjectArea)}</span>`);
    if (course?.educationalLevel) metaParts.push(`<span><strong>Level:</strong> ${esc(course.educationalLevel)}</span>`);
    if (course?.grade) metaParts.push(`<span><strong>Grade:</strong> ${esc(course.grade)}</span>`);
    if (course?.totalHours) metaParts.push(`<span><strong>Duration:</strong> ${esc(course.totalHours)} hours</span>`);

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    @page {
      size: A4;
      margin: 2cm;
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-size: 10pt;
        color: #718096;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
    }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #2d3748;
      background: #ffffff;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      width: 210mm; /* A4 width */
      margin: 0;
      padding: 0;
    }
    
    .cover-page {
      page-break-after: always;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      min-height: 267mm; /* A4 height minus margin */
      width: 180mm; /* A4 width minus margin */
      margin: 1.5cm;
      padding: 3cm;
      background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
      color: white;
      box-sizing: border-box;
    }
    
    .cover-title {
      font-size: 42pt;
      font-weight: 700;
      margin-bottom: 1cm;
      text-align: center;
      line-height: 1.2;
      text-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .cover-meta {
      font-size: 14pt;
      text-align: center;
      opacity: 0.95;
      margin-top: 1cm;
      margin-bottom: 1.5cm;
    }
    
    .cover-meta span {
      display: inline-block;
      margin: 0 0.5cm;
    }
    
    .cover-description {
      font-size: 13pt;
      line-height: 1.8;
      color: rgba(255, 255, 255, 0.95);
      margin-top: 1.5cm;
      padding: 1.2cm;
      background: rgba(255, 255, 255, 0.15);
      border-left: 4px solid rgba(255, 255, 255, 0.5);
      border-radius: 4px;
      max-width: 100%;
      text-align: left;
      backdrop-filter: blur(10px);
    }
    
    .content-page {
      padding: 1.5cm 0;
    }
    
    .course-description {
      font-size: 12pt;
      line-height: 1.8;
      color: #4a5568;
      margin-bottom: 2cm;
      padding: 1cm;
      background: #f7fafc;
      border-left: 4px solid #f59e0b;
      border-radius: 4px;
    }
    
    .section-header {
      font-size: 24pt;
      font-weight: 700;
      color: #1a202c;
      margin: 0 0 1cm 0;
      padding-bottom: 0.5cm;
      border-bottom: 3px solid #f59e0b;
      page-break-after: avoid;
    }
    
    .topic-section {
      margin-bottom: 2.5cm;
      margin-top: 1cm;
      page-break-inside: avoid;
      page-break-before: auto;
    }
    
    .topic-header {
      display: flex;
      align-items: center;
      gap: 1cm;
      margin-bottom: 1cm;
      padding: 0.8cm 1cm;
      background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
      color: white;
      border-radius: 8px;
      page-break-after: avoid;
    }
    
    .topic-number {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      width: 2.5cm;
      height: 2.5cm;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      font-size: 22pt;
      font-weight: 700;
      flex-shrink: 0;
      padding-top: 0.4cm;
      line-height: 1;
    }
    
    .topic-title {
      font-size: 20pt;
      font-weight: 700;
      margin: 0;
      flex: 1;
    }
    
    .activities-container {
      margin-top: 1cm;
    }
    
    .activity-card {
      margin-bottom: 2cm;
      margin-top: 0.5cm;
      padding: 1cm;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-left: 4px solid #4299e1;
      border-radius: 6px;
      page-break-inside: avoid;
      page-break-before: auto;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    }
    
    .activity-header {
      margin-bottom: 0.8cm;
      page-break-inside: avoid;
      page-break-after: avoid;
    }
    
    .activity-title {
      font-size: 16pt;
      font-weight: 700;
      color: #1a202c;
      margin-bottom: 0.5cm;
      page-break-after: avoid;
    }
    
    .badges {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5cm;
      margin-top: 0.5cm;
      page-break-inside: avoid;
      page-break-after: avoid;
    }
    
    .goal-badge, .actor-badge, .resource-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.2cm 0.7cm 0.35cm 0.7cm;
      border-radius: 20px;
      font-size: 11pt;
      font-weight: 600;
      line-height: 1;
      page-break-inside: avoid;
    }
    
    .goal-badge {
      background: #ede9fe;
      color: #6d28d9;
    }
    
    .actor-badge {
      background: #ffe4e6;
      color: #be123c;
    }
    
    .resource-badge {
      background: #dcfce7;
      color: #166534;
    }
    
    .goal-box {
      display: flex;
      align-items: flex-start;
      padding: 0.8cm;
      margin-bottom: 0.8cm;
      background: #f3f4f6;
      border-left: 4px solid #6d28d9;
      border-radius: 4px;
      page-break-inside: avoid;
    }
    
    .goal-content {
      flex: 1;
      font-size: 14pt;
      color: #4a5568;
      line-height: 1.6;
    }
    
    .goal-content strong {
      color: #6d28d9;
      font-weight: 700;
      font-size: 14pt;
    }
    
    .activity-description {
      font-size: 11pt;
      line-height: 1.8;
      color: #2d3748;
      margin-bottom: 0.8cm;
      orphans: 3;
      widows: 3;
      page-break-inside: avoid;
    }
    
    .meta-info {
      font-size: 10pt;
      color: #4a5568;
      margin-top: 0.5cm;
      padding-top: 0.5cm;
      border-top: 1px solid #e2e8f0;
      page-break-inside: avoid;
      page-break-before: avoid;
    }
    
    .meta-info strong {
      color: #2d3748;
      font-weight: 600;
    }
    
    .meta-info em {
      color: #718096;
      font-style: italic;
    }
    
    .empty-state {
      font-style: italic;
      color: #a0aec0;
      text-align: center;
      padding: 1cm;
    }
    
    .metadata-row {
      display: flex;
      flex-wrap: wrap;
      gap: 1cm;
      justify-content: center;
      margin: 1cm 0;
      font-size: 11pt;
      color: #4a5568;
    }
    
    .metadata-row span {
      padding: 0.3cm 0.6cm;
      background: #f7fafc;
      border-radius: 4px;
    }
    
    @media print {
      .topic-section {
        page-break-inside: avoid;
        page-break-before: auto;
      }
      
      .activity-card {
        page-break-inside: avoid;
        page-break-before: auto;
        orphans: 3;
        widows: 3;
      }
      
      .activity-header {
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      
      .badges {
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      
      .goal-box {
        page-break-inside: avoid;
        page-break-after: avoid;
      }
      
      .meta-info {
        page-break-inside: avoid;
        page-break-before: avoid;
      }
      
      .activity-description {
        page-break-inside: avoid;
        orphans: 3;
        widows: 3;
      }
      
      body {
        orphans: 3;
        widows: 3;
      }
    }
  </style>
</head>
<body>
  <div class="cover-page">
    <h1 class="cover-title">${title}</h1>
    ${metaParts.length > 0 ? `<div class="cover-meta">${metaParts.join('')}</div>` : ''}
    ${course?.description ? `<div class="cover-description">${esc(course.description)}</div>` : ''}
  </div>
  
  <div class="content-page">
    <h2 class="section-header">Topics & Activities</h2>
    
    ${topicsHtml || '<p class="empty-state">No topics yet.</p>'}
  </div>
</body>
</html>`;
  }, [course]);

  const handleExportPdf = useCallback(() => {
    (async () => {
      if (!course) return;
      try {
        // Show loading toast
        toast({ title: 'Generating PDF...', description: 'This may take a moment' });

        // Import jsPDF and html2canvas
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;

        // Build the HTML
        const html = buildCoursePdfHtml();

        // Create an iframe to properly render the HTML document
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.left = '0';
        iframe.style.top = '0';
        iframe.style.width = '794px'; // A4 width in pixels (210mm at 96 DPI)
        iframe.style.height = '5000px'; // Large enough to show all content
        iframe.style.border = 'none';
        iframe.style.visibility = 'hidden';
        iframe.style.overflow = 'visible';
        document.body.appendChild(iframe);

        // Write HTML to iframe
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();

        // Wait for iframe content to load and render - optimized with faster checks
        await new Promise(resolve => {
          const checkReady = () => {
            const body = iframeDoc.body;
            if (body && body.children.length > 0) {
              // Use requestAnimationFrame to ensure rendering is complete
              requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
              });
            } else {
              setTimeout(checkReady, 50);
            }
          };

          if (iframe.contentWindow) {
            iframe.onload = () => {
              setTimeout(checkReady, 100);
            };
            // Reduced fallback timeout
            setTimeout(() => {
              checkReady();
              setTimeout(resolve, 300);
            }, 800);
          } else {
            setTimeout(checkReady, 100);
            setTimeout(resolve, 800);
          }
        });

        // Wait for fonts (if available) - no extra delay
        if (iframeDoc.fonts && iframeDoc.fonts.ready) {
          await iframeDoc.fonts.ready;
        }

        // Get the body element from iframe
        const bodyElement = iframeDoc.body;
        if (!bodyElement) {
          throw new Error('Failed to render HTML content');
        }

        // Force layout recalculation
        bodyElement.style.height = 'auto';
        const actualHeight = Math.max(bodyElement.scrollHeight, bodyElement.offsetHeight, 1123);
        const actualWidth = bodyElement.scrollWidth || 794;

        // Update iframe height to match content
        iframe.style.height = `${actualHeight}px`;
        // Reduced wait time - just one frame for layout
        await new Promise(resolve => requestAnimationFrame(resolve));

        // Convert to canvas - optimized settings for speed
        const canvas = await html2canvas(bodyElement, {
          scale: 1.5, // Reduced from 2 for faster rendering (still good quality)
          useCORS: true,
          letterRendering: false, // Disabled for speed
          logging: false,
          backgroundColor: '#ffffff',
          width: actualWidth,
          height: actualHeight,
          windowWidth: actualWidth,
          windowHeight: actualHeight,
          scrollX: 0,
          scrollY: 0,
          removeContainer: false, // Keep container for faster processing
          imageTimeout: 0 // Don't wait for images
        });

        // Calculate PDF dimensions
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Create PDF
        const pdf = new jsPDF('p', 'mm', 'a4');

        // Get activity card positions to avoid splitting them across pages
        const activityCards = bodyElement.querySelectorAll('.activity-card');
        const activityPositions = [];

        // Get topic section positions to ensure they start at the top of a page
        const topicSections = bodyElement.querySelectorAll('.topic-section');
        const topicPositions = [];

        // html2canvas renders at scale: 1.5, so positions need to be multiplied by 1.5
        const html2canvasScale = 1.5;

        activityCards.forEach((card) => {
          // Get position relative to the body element
          let top = 0;
          let element = card;

          // Walk up the DOM tree to calculate absolute position
          while (element && element !== bodyElement) {
            top += element.offsetTop;
            element = element.offsetParent;
          }

          // Fallback: use getBoundingClientRect if offsetTop doesn't work
          if (top === 0) {
            const rect = card.getBoundingClientRect();
            const bodyRect = bodyElement.getBoundingClientRect();
            top = rect.top - bodyRect.top;
          }

          const height = card.offsetHeight || card.getBoundingClientRect().height;
          const bottom = top + height;

          // Store positions in HTML coordinates (will be converted to canvas coordinates later)
          activityPositions.push({ top, bottom, height });
        });

        topicSections.forEach((section) => {
          // Get position relative to the body element
          let top = 0;
          let element = section;

          // Walk up the DOM tree to calculate absolute position
          while (element && element !== bodyElement) {
            top += element.offsetTop;
            element = element.offsetParent;
          }

          // Fallback: use getBoundingClientRect if offsetTop doesn't work
          if (top === 0) {
            const rect = section.getBoundingClientRect();
            const bodyRect = bodyElement.getBoundingClientRect();
            top = rect.top - bodyRect.top;
          }

          // Store topic position - we only need the top to ensure it starts at page top
          topicPositions.push({ top });
        });

        // Sort by top position
        activityPositions.sort((a, b) => a.top - b.top);
        topicPositions.sort((a, b) => a.top - b.top);

        // Split canvas into pages - avoid splitting activities
        let yPosition = 0;
        let sourceY = 0;
        const pageWidth = imgWidth;
        const pageHeightPx = (pageHeight * canvas.width) / imgWidth; // Page height in canvas pixels

        while (sourceY < canvas.height) {
          // Calculate where the page would end in canvas coordinates
          const pageEndY = sourceY + pageHeightPx;

          // Check if any activity would be split by this page break
          let adjustedPageEndY = pageEndY;
          for (const activity of activityPositions) {
            // Convert activity positions from HTML pixels to canvas pixels
            // html2canvas renders at scale: 2, so multiply by 2
            const activityTopCanvas = activity.top * html2canvasScale;
            const activityBottomCanvas = activity.bottom * html2canvasScale;

            // Check if the page break would split this activity
            // An activity is split if it starts on current page but doesn't fit entirely
            const activityStartsOnPage = activityTopCanvas >= sourceY && activityTopCanvas < pageEndY;
            const activityEndsAfterPage = activityBottomCanvas > pageEndY;

            if (activityStartsOnPage && activityEndsAfterPage) {
              // This activity would be split - move page break before it starts
              adjustedPageEndY = Math.min(adjustedPageEndY, activityTopCanvas);
            }
          }

          // Check if any topic would appear too close to the bottom of the page
          // Topics can appear below other content, but should not be at the bottom of a page
          for (const topic of topicPositions) {
            // Convert topic position from HTML pixels to canvas pixels
            const topicTopCanvas = topic.top * html2canvasScale;

            // Check if this topic would appear on the current page
            const topicOnCurrentPage = topicTopCanvas >= sourceY && topicTopCanvas < pageEndY;

            if (topicOnCurrentPage) {
              // Calculate how much space would be left on the page after the topic starts
              const spaceAfterTopic = pageEndY - topicTopCanvas;

              // Define minimum space needed after a topic (in canvas pixels)
              // This ensures the topic has enough room and doesn't appear cramped at the bottom
              // Using 50% of page height as minimum space (roughly 148mm or half of A4 page)
              const minSpaceAfterTopic = pageHeightPx * 0.5;

              // If there's not enough space after the topic, move it to the next page
              if (spaceAfterTopic < minSpaceAfterTopic) {
                // Move page break before the topic starts
                adjustedPageEndY = Math.min(adjustedPageEndY, topicTopCanvas);
              }
            }
          }

          // Ensure we don't go backwards
          if (adjustedPageEndY <= sourceY) {
            adjustedPageEndY = pageEndY; // Fallback to original if adjustment would cause issues
          }

          // Calculate content height for this page
          const remainingHeight = canvas.height - sourceY;
          const pageContentHeight = Math.min(adjustedPageEndY - sourceY, remainingHeight);

          // Create a temporary canvas for this page
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = pageContentHeight;
          const ctx = pageCanvas.getContext('2d');

          // Fill with white background first
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

          // Draw the portion of the original canvas for this page
          ctx.drawImage(
            canvas,
            0, sourceY, canvas.width, pageContentHeight,
            0, 0, canvas.width, pageContentHeight
          );

          // Calculate dimensions for PDF
          const pageImgHeight = (pageContentHeight * imgWidth) / canvas.width;

          // Add page (except for first page which is already created)
          if (yPosition > 0) {
            pdf.addPage();
          }

          // Add image to PDF - reduced quality slightly for faster encoding
          pdf.addImage(
            pageCanvas.toDataURL('image/jpeg', 0.85),
            'JPEG',
            0,
            0,
            pageWidth,
            pageImgHeight
          );

          // Move to next page
          sourceY = adjustedPageEndY;
          yPosition += pageHeight;
        }

        // Add page numbers
        const totalPages = pdf.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setTextColor(130, 130, 130);
          pdf.text(
            `Page ${i} of ${totalPages}`,
            pageWidth - 20,
            pageHeight - 10
          );
        }

        // Save PDF
        pdf.save(`${course.title || 'course'}.pdf`);

        // Clean up
        document.body.removeChild(iframe);

        toast({ title: 'Exported', description: 'PDF file downloaded' });
      } catch (err) {
        console.error('PDF export error:', err);
        toast({ title: 'PDF export failed', description: err.message || 'Please try again.', variant: 'destructive' });
      }
    })();
  }, [course, buildCoursePdfHtml, toast]);

  const handleExportEclass = useCallback(async () => {
    if (!course || !effectiveCourseId) return;
    try {
      const sourcePayload = transformCourseDataToCanonicalCdm();
      if (!sourcePayload) {
        toast({ title: 'Export failed', description: 'No course data available to export.', variant: 'destructive' });
        return;
      }
      await courseAPI.exportEclass(effectiveCourseId, sourcePayload);
      toast({ title: 'Exported', description: 'Course sent to eClass endpoint successfully.' });
    } catch (e) {
      const backendMessage = e?.response?.data?.detail;
      toast({
        title: 'Export failed',
        description: backendMessage || 'Could not send course to eClass endpoint.',
        variant: 'destructive'
      });
    }
  }, [course, effectiveCourseId, toast, transformCourseDataToCanonicalCdm]);


  return {
    handleExportJson,
    handleExportCdm,
    handleExportDoc,
    handleExportPdf,
    handleExportEclass,
    // Keep alias to avoid breaking callers not yet migrated.
    handleExportMoodle: handleExportEclass,
  };
}

